import React from 'react';
import { GetSocialAnalyticsSummaryWithPreviousPeriodQuery } from '@frontend/gql/social/graphql';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Card, CardContent } from '@frontend/shadcn/components/ui/card';
import { DateType } from '../../types';
import ReachCard from '../reach-card';
import ImpressionsCard from '../impressions-card';
import EngagementCard from '../engagement-card';
import TMVCard from '../tmv-card';
import FollowerGrowthCard from '../follower-growth-card';
import {
  PostsMetric,
  MembersMetric,
  SavesMetric,
  LikesMetric,
  CommentsMetric,
  SharesMetric,
  ViewsMetric,
} from '../secondary-metrics';

interface SummaryTabProps {
  data: GetSocialAnalyticsSummaryWithPreviousPeriodQuery | undefined;
  isLoading: boolean;
  startDate?: string;
  endDate?: string;
  dateType?: DateType;
}

export const SummaryTab: React.FC<SummaryTabProps> = ({ data, isLoading, startDate, endDate, dateType }) => {
  const totalInsights = data?.client?.socialAnalytics?.totalInsights;
  const previousPeriodTotalInsights = data?.client?.socialAnalytics?.previousPeriodTotalInsights;

  if (isLoading) {
    return (
      <div className="p-4 space-y-6">
        {/* Primary metrics skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, index) => (
            <Skeleton key={index} className="h-32 w-full" />
          ))}
        </div>
        {/* Secondary metrics skeleton */}
        <Card>
          <CardContent className="p-4">
            <div className="grid grid-cols-2 lg:grid-cols-7 gap-4">
              {[...Array(7)].map((_, index) => (
                <Skeleton key={index} className="h-16 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
        {/* Chart cards skeleton */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Skeleton className="h-80 w-full" />
          <Skeleton className="h-80 w-full" />
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* Primary metrics */}
      <div
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4"
        role="region"
        aria-label="Key social media metrics"
      >
        <ReachCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
        />
        <ImpressionsCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
        />
        <EngagementCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
        />
        <TMVCard
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
        />
      </div>

      {/* Secondary metrics */}
      <Card>
        <CardContent className="p-4">
          <div
            className="grid grid-cols-2 lg:grid-cols-7 gap-4"
            role="region"
            aria-label="Secondary social media metrics"
          >
            <PostsMetric data={totalInsights} />
            <MembersMetric data={totalInsights} />
            <SavesMetric data={totalInsights} />
            <LikesMetric data={totalInsights} />
            <CommentsMetric data={totalInsights} />
            <ViewsMetric data={totalInsights} />
            <SharesMetric data={totalInsights} />
          </div>
        </CardContent>
      </Card>

      {/* Chart cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" role="region" aria-label="Analytics charts">
        <FollowerGrowthCard
          className="col-span-1"
          startDate={startDate}
          endDate={endDate}
          dateType={dateType}
          data={totalInsights}
          previousPeriodData={previousPeriodTotalInsights}
        />
        {/* TMV chart will go here in the future */}
        <div className="col-span-1">{/* Placeholder for Total Media Value chart */}</div>
      </div>
    </div>
  );
};
